import { Box } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { EntitySelect, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { getStrategyOptions, getStrategyType, type UIType } from './manualApproveHandlerUtils'

export interface StrategySelectorEmployee {
  id: number
  fullName: string
  avatar?: { url?: string | null } | null
}

export interface StrategySelectorTeam {
  id: number
  name: string
}

interface StrategySelectorProps {
  strategyValue: string
  onStrategyChange: (value: string) => void
  onEmployeeSelect: (selection: EntitySelectEntity[] | EntitySelectEntity | null) => void
  onTeamSelect: (selection: EntitySelectEntity[] | EntitySelectEntity | null) => void
  selectedEmployees: Array<{ id: number; name: string; avatar?: string }>
  selectedTeams: Array<{ id: number; name: string }>
  availableEmployees: StrategySelectorEmployee[]
  availableTeams: StrategySelectorTeam[]
  employeesLoading: boolean
  teamsLoading: boolean
  singleEmployeeSelection?: boolean
  strategyOptions?: Array<{ label: string; value: string; ui_type: UIType }>
  placeholder?: string
}

export const StrategySelector = ({
  strategyValue,
  onStrategyChange,
  onEmployeeSelect,
  onTeamSelect,
  selectedEmployees,
  selectedTeams,
  availableEmployees,
  availableTeams,
  employeesLoading,
  teamsLoading,
  singleEmployeeSelection = true,
  strategyOptions,
  placeholder,
}: StrategySelectorProps) => {
  const options = strategyOptions ?? getStrategyOptions()
  const strategyUIType = getStrategyType(strategyValue)

  return (
    <Box gap="s8" width="full">
      <F0Select
        size="md"
        label="Strategy selector"
        hideLabel
        value={strategyValue}
        onChange={onStrategyChange}
        options={options.map(({ ui_type: _, ...option }) => option)}
        placeholder={placeholder}
      />

      {strategyUIType === 'manual_selected_employees' && (
        <EntitySelect
          label={i18n.t('workflows.sidepanel.approval.strategies.selectors.employees')}
          hideLabel
          size="md"
          loading={employeesLoading}
          entities={availableEmployees.map((emp) => ({
            id: emp.id,
            name: emp.fullName,
            avatar: buildUrl(emp.avatar?.url),
            expanded: false,
          }))}
          notFoundSubtitle={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.employees_not_found_subtitle'
          )}
          notFoundTitle={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.employees_not_found_title'
          )}
          onGroupChange={() => {}}
          onItemExpandedChange={() => {}}
          onSelect={onEmployeeSelect}
          searchPlaceholder={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.employees_search_placeholder'
          )}
          selectedEntities={selectedEmployees}
          selectedLabel={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.employees_selected_label'
          )}
          singleSelector={singleEmployeeSelection}
          placeholder={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.employees_placeholder'
          )}
          selectedItemsCopy={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.employees_selected_copy'
          )}
          groups={[]}
          selectedGroup="all"
          clearLabel={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.employees_clear_label'
          )}
          selectAllLabel={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.employees_select_all_label'
          )}
        />
      )}

      {strategyUIType === 'team' && (
        <EntitySelect
          label={i18n.t('workflows.sidepanel.approval.strategies.selectors.teams')}
          hideLabel
          size="md"
          loading={teamsLoading}
          entities={availableTeams.map((team) => ({
            id: team.id,
            name: team.name,
            expanded: false,
          }))}
          notFoundSubtitle={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.teams_not_found_subtitle'
          )}
          notFoundTitle={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.teams_not_found_title'
          )}
          onGroupChange={() => {}}
          onItemExpandedChange={() => {}}
          onSelect={onTeamSelect}
          searchPlaceholder={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.teams_search_placeholder'
          )}
          selectedEntities={selectedTeams}
          selectedLabel={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.teams_selected_label'
          )}
          singleSelector
          placeholder={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.teams_placeholder'
          )}
          selectedItemsCopy={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.teams_selected_copy'
          )}
          groups={[]}
          selectedGroup="all"
          clearLabel={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.teams_clear_label'
          )}
          selectAllLabel={i18n.t(
            'workflows.sidepanel.approval.strategies.selectors.teams_select_all_label'
          )}
        />
      )}
    </Box>
  )
}

